import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ProfileLink } from "@/components/ProfileDrawer";
import { formatDateTimeWAT, formatDateDDMMYYYY } from "@/lib/format";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

export const Shifts = () => {
  const [shifts, setShifts] = useState([]);
  const [guards, setGuards] = useState([]);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ site_id: "", guard_id: "", start_at: "", end_at: "", notes: "" });

  const load = useCallback(async () => {
    const r = await api.get("/shifts");
    setShifts(r.data);
  }, []);

  useEffect(() => {
    load();
    api.get("/guards").then(r => setGuards(r.data));
    api.get("/sites").then(r => setSites(r.data));
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        guard_id: form.guard_id || null,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
      };
      await api.post("/shifts", payload);
      toast.success("Shift scheduled");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Failed to create shift");
    }
  };

  const guardName = (id) => guards.find(g => g.id === id)?.full_name || "Open shift";
  const siteName = (id) => sites.find(s => s.id === id)?.name || id;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-muted" style={{ fontSize: 14 }}>{shifts.length} shifts scheduled</div>
        <button className="btn-primary inline-flex items-center gap-2" onClick={() => setOpen(true)} data-testid="add-shift-btn">
          <Plus size={14} /> Schedule shift
        </button>
      </div>
      <div className="s-card" style={{ padding: 0 }}>
        <table className="s-table">
          <thead><tr><th>Date</th><th>Guard</th><th>Site</th><th>Start</th><th>End</th><th>Status</th></tr></thead>
          <tbody>
            {shifts.slice(0, 100).map(s => (
              <tr key={s.id} data-testid={`shift-row-${s.id}`}>
                <td className="mono">{formatDateDDMMYYYY(s.start_at)}</td>
                <td style={{ color: s.guard_id ? "#fff" : "#C9A84C" }}>
                  {s.guard_id ? <ProfileLink kind="guard" id={s.guard_id}>{guardName(s.guard_id)}</ProfileLink> : guardName(s.guard_id)}
                </td>
                <td className="text-muted">{siteName(s.site_id)}</td>
                <td className="mono">{formatDateTimeWAT(s.start_at).split(" ")[1] || ""} {formatDateTimeWAT(s.start_at).split(" ")[2] || ""}</td>
                <td className="mono">{formatDateTimeWAT(s.end_at).split(" ")[1] || ""} {formatDateTimeWAT(s.end_at).split(" ")[2] || ""}</td>
                <td><StatusBadge status={s.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 100,
        }}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="s-card" style={{ width: 420, maxWidth: "90vw" }}>
            <div className="flex justify-between items-center mb-4">
              <div className="h-section">New shift</div>
              <button type="button" onClick={() => setOpen(false)} className="text-muted"><X size={18} /></button>
            </div>
            <label className="text-muted" style={{ fontSize: 12 }}>Site</label>
            <select required value={form.site_id} onChange={(e) => setForm({...form, site_id: e.target.value})} className="s-input mt-1 mb-3" data-testid="sh-site">
              <option value="">Select site...</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <label className="text-muted" style={{ fontSize: 12 }}>Guard (leave blank for open shift)</label>
            <select value={form.guard_id} onChange={(e) => setForm({...form, guard_id: e.target.value})} className="s-input mt-1 mb-3" data-testid="sh-guard">
              <option value="">Open shift</option>
              {guards.map(g => <option key={g.id} value={g.id}>{g.full_name}</option>)}
            </select>
            <label className="text-muted" style={{ fontSize: 12 }}>Start</label>
            <input type="datetime-local" required value={form.start_at} onChange={(e) => setForm({...form, start_at: e.target.value})} className="s-input mt-1 mb-3" />
            <label className="text-muted" style={{ fontSize: 12 }}>End</label>
            <input type="datetime-local" required value={form.end_at} onChange={(e) => setForm({...form, end_at: e.target.value})} className="s-input mt-1 mb-4" />
            <button type="submit" className="btn-primary w-full" data-testid="sh-submit">Schedule</button>
          </form>
        </div>
      )}
    </div>
  );
};
